import { Component, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import * as fromCumsActions from '../../contract-cumulative/store/contract-cumulative.actions';
import * as fromCumsReducer from '../../contract-cumulative/store/contract-cumulative.reducer';
import * as fromDataServices from '../../data-services';
import { DeferBilling } from '../../data-services/defer-billing';
import { ExpenseItem } from '../../expense-item/store/expense-item.model';
import * as fromEIReducer from '../../expense-item/store/expense-item.reducer';
import * as fromLookups from '../../lookups';
import { IDirectReport, LoadServiceItems } from '../../lookups';
import { PayrollItem } from '../../payroll/item/store';
import * as fromCommon from '../../shared';
import * as fromShared from '../../shared';
import { uniqBy } from '../../shared';
import { LoggingService } from '../../shared/services/logging';
import * as fromUser from '../../user-info';
import { IUserInfo } from '../../user-info';
import { Item } from '../defer-billing-presenter/defer-billing-presenter.component';
@Component({
    selector: 'ef-defer-billing-feature',
    templateUrl: './defer-billing-feature.component.html',
    styleUrls: ['./defer-billing-feature.component.scss'],
    standalone: false
})
export class DeferBillingFeatureComponent implements OnInit {
    process$: Observable<fromShared.Process>;

    public billing$: Observable<DeferBilling[]>;
    public serviceitems$: Observable<fromLookups.IServiceItem[]>;
    public directreports$: Observable<IDirectReport[]>;
    public tasks$: Observable<fromLookups.ITask[]>;
    public userGroups$: Observable<fromLookups.IUserGroup[]>;
    public contracts$: Observable<fromDataServices.Contract.Contract[]>;
    public chargenumbers$: Observable<
        fromDataServices.ChargeNumber.ChargeNumber[]
    >;
    public txntypes$: Observable<fromLookups.CalcType[]>;
    public payrollitems$: Observable<PayrollItem[]>;
    public userInfo$: Observable<IUserInfo>;
    public expenseitems$: Observable<ExpenseItem[]>;
    public items$: Observable<Item[]>;
    public userInfo: IUserInfo;

    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);

    active = true;
    IsLoading: boolean;
    billingDataService: EntityCollectionService<DeferBilling>;
    contractDataService: EntityCollectionService<fromDataServices.Contract.Contract>;
    chargeNumberDataService: EntityCollectionService<fromDataServices.ChargeNumber.ChargeNumber>;

    constructor(
        public entityServices: EntityServices,
        private log: LoggingService,
        private store: Store
    ) {
        this.billingDataService =
            entityServices.getEntityCollectionService<DeferBilling>(
                fromDataServices.DeferBilling.entityName
            );
        this.contractDataService =
            entityServices.getEntityCollectionService<fromDataServices.Contract.Contract>(
                fromDataServices.Contract.entityName
            );
        this.chargeNumberDataService =
            entityServices.getEntityCollectionService<fromDataServices.ChargeNumber.ChargeNumber>(
                fromDataServices.ChargeNumber.entityName
            );
    }

    ngOnInit() {
        // this.billingDataService.getWithQuery({
        //     StartDate: moment(new Date()).format('MM/DD/YYYY'),
        //     EndDate: moment(new Date()).format('MM/DD/YYYY'),
        //     Contracts: []
        // });
        this.contractDataService.getAll();
        // this.chargeNumberDataService.getAll();
        // this.store.dispatch(fromEIActions.getExpenseItems());
        // this.store.dispatch(fromStore.getPayrollItems());
        this.store.dispatch(fromCumsActions.getTxnTypes());
        this.store.dispatch(new fromLookups.LoadUserGroups());
        this.store.dispatch(new fromLookups.LoadTasks());
        this.store.dispatch(new LoadServiceItems(false, true, true));
        this.setupObservables();
        this.setupSubscriptions();
    }

    ngOnDestroy() {
        this.active = false;
        this.billingDataService.clearCache();
        this.billing$ = null;
    }

    setupObservables() {
        // this.companysettings$ = this.recalculateSettingsDataService.entities$.pipe(
        //     map(x => clone(x))
        // );

        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));

        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.expenseitems$ = this.store.pipe(
            select(fromEIReducer.selectAll),
            map(x => clone(x))
        );

        this.billing$ = this.billingDataService.entities$.pipe(
            map(c => clone(c))
        );

        this.userGroups$ = this.store.pipe(
            select(fromLookups.getUserGroupsLookup)
        );

        this.payrollitems$ = this.store.pipe(
            select(fromLookups.getPayrollItemsLookup),
            map(x => clone(x) as PayrollItem[]),
            map(payrollItems =>
                payrollItems
                    .filter(x => x.IsActive === true)
                    .sort((a, b) => a.PayrollItem.localeCompare(b.PayrollItem))
            ),
            map(items => uniqBy(items, x => x.PIListID) as PayrollItem[])
        );

        this.directreports$ = this.store.pipe(
            select(fromLookups.getDirectReportsLookup),
            map(x => clone(x)),
            map(users => users.filter(x => x.IsActive == true)),
            map(x =>
                x.sort((a, b) => a.EmployeeName.localeCompare(b.EmployeeName))
            )
        );

        this.serviceitems$ = this.store.pipe(
            select(fromLookups.getServiceItemsLookup),
            map(x => clone(x)),
            map(x =>
                x.sort((a, b) => a.ServiceItem.localeCompare(b.ServiceItem))
            )
        );

        this.tasks$ = this.store.pipe(
            select(fromLookups.getTasksLookup),
            map(x => clone(x)),
            map(x =>
                x.sort((a, b) => a.TaskLongName.localeCompare(b.TaskLongName))
            )
        );

        this.txntypes$ = this.store.pipe(
            select(fromCumsReducer.selectTxnTypes),
            map(x => clone(x)),
            map(x => x.sort((a, b) => a.CalcDesc.localeCompare(b.CalcDesc)))
        );

        this.contracts$ = this.contractDataService.entities$.pipe(
            map(x => clone(x)),
            map(x =>
                x.sort((a, b) => a.ContractDesc.localeCompare(b.ContractDesc))
            )
        );

        this.chargenumbers$ = this.chargeNumberDataService.entities$.pipe(
            map(x => clone(x)),
            map(x => x.sort((a, b) => a.FullName.localeCompare(b.FullName)))
        );
    }

    setupSubscriptions() {
        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
        this.billing$.pipe(takeWhile(() => this.active)).subscribe(billing => {
            // billing = billing.filter(items => !!items);
            if (billing.length > 0) {
                this.IsLoading = false;
            } else {
                this.IsLoading = true;
            }
        });

        this.userInfo$
            .pipe(takeWhile(() => this.active))
            .subscribe(userInfo => {
                this.userInfo = userInfo;
                this.store.dispatch(new fromLookups.LoadDirectReports({user: userInfo, activeOnly: false }));
            });
        // combineLatest([this.serviceitems$, this.expenseitems$])
        //     .pipe(takeWhile(() => this.active))
        //     .subscribe(([si, ei]) => {
        //         let siarr: any[] = si;
        //         siarr = siarr.map(x => ({
        //             IListID: x.SIListID,
        //             Item: x.ServiceItem,
        //             IsActive: x.IsActive
        //         }));
        //         let eiarr: any[] = ei;
        //         eiarr = eiarr.map(x => ({
        //             IListID: x.EIListID,
        //             Item: x.ExpenseItem,
        //             IsActive: x.IsActive
        //         }));
        //         const items: Item[] = siarr.concat(eiarr);
        //         this.items$ = of(items);
        //     });
    }

    onLoadBilling($event) {
        this.log.verbose({
            type: 'DeferBillingFeatureComponent:onLoadBilling',
            $event
        });
        this.billingDataService.clearCache();
        if ($event != null) {
            this.billingDataService.getWithQuery($event);
        } else {
            this.billingDataService.getWithQuery(null);
        }
        this.IsLoading = true;
    }

    onSaveBilling($event) {
        this.log.verbose({
            type: 'DeferBillingFeatureComponent:onSaveBilling'
        });
        this.billingDataService.upsert($event);
    }

    onDeleteBilling($event) {
        this.billingDataService.delete($event);
    }

    onLoadChargeNumbers($event) {
        this.chargeNumberDataService.clearCache();
        this.chargeNumberDataService.getWithQuery($event);
    }
}
